import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { RefreshCw, Store, Check } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listStores } from "@/lib/api/admin";
import { SpinnerCenter } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/Pagination";
import { EmptyState } from "@/components/EmptyState";
import { StoreCard } from "./components/StoreCard";
import { PAGE_SIZE } from "@/lib/constants";
import { cn } from "@/lib/utils";

// Colors match disca-admin-flutter AppColors: success / statusSuspended / warning
// "inactive" means status=active but visible=false (not yet publicly visible).
// "pending-approval" is a subfilter under Inactive showing active stores without approval.
const STATUS_FILTERS = [
  { value: "active", label: "Active", color: "#22C55E" },
  { value: "inactive", label: "Inactive", color: "#D97706" },
  { value: "suspended", label: "Suspended", color: "#EA580C" },
  { value: "hidden", label: "Hidden", color: "#8B5CF6" },
] as const;

// Secondary subfilter chips shown inside the "Inactive" tab.
const INACTIVE_SUBFILTERS = [
  { value: "all", label: "All", color: "#64748B" },
  { value: "pending-approval", label: "Pending Approval", color: "#F59E0B" },
] as const;

function FilterChip({
  label,
  color,
  isSelected,
  onClick,
}: {
  label: string;
  color: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  const style: React.CSSProperties = isSelected
    ? { borderColor: color, backgroundColor: `${color}33`, color }
    : hovered
      ? { borderColor: color, backgroundColor: `${color}1a`, color }
      : {};

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        "inline-flex h-8 items-center gap-1 rounded-full border px-3 text-sm transition-colors",
        isSelected ? "font-semibold" : "border-border text-muted-foreground"
      )}
      style={style}
    >
      {isSelected && <Check className="size-3.5" />}
      {label}
    </button>
  );
}

export function StoresPage() {
  const user = useAdminAuth();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") ?? "active";
  const [offset, setOffset] = useState(0);
  // Allow deep links like /stores?status=inactive&sub=pending-approval (dashboard "Pending Approval" card).
  const [inactiveSub, setInactiveSub] = useState(searchParams.get("sub") ?? "all");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["stores", status, inactiveSub, offset],
    queryFn: async () => {
      if (status === "inactive") {
        return listStores(user!, {
          status: "active",
          visible: false,
          pending_approval: inactiveSub === "pending-approval" ? true : undefined,
          limit: PAGE_SIZE,
          offset,
        });
      }
      if (status === "active") {
        return listStores(user!, { status: "active", visible: true, limit: PAGE_SIZE, offset });
      }
      return listStores(user!, { status, limit: PAGE_SIZE, offset });
    },
    enabled: !!user,
  });

  function handleFilterChange(s: string) {
    setSearchParams({ status: s });
    setOffset(0);
    setInactiveSub("all");
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Stores</h1>
          <p className="text-sm text-muted-foreground">
            {data ? `${data.total} stores` : "Manage stores"}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="size-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Status filter chips — outline style matching disca-admin-flutter */}
      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map((f) => (
          <FilterChip
            key={f.value}
            label={f.label}
            color={f.color}
            isSelected={status === f.value}
            onClick={() => handleFilterChange(f.value)}
          />
        ))}
      </div>

      {/* Inactive subfilter — only visible when the Inactive tab is selected */}
      {status === "inactive" && (
        <div className="flex gap-2 flex-wrap pl-1 border-l-2 border-border ml-1">
          {INACTIVE_SUBFILTERS.map((f) => (
            <FilterChip
              key={f.value}
              label={f.label}
              color={f.color}
              isSelected={inactiveSub === f.value}
              onClick={() => {
                setInactiveSub(f.value);
                setOffset(0);
              }}
            />
          ))}
        </div>
      )}

      {/* Store grid */}
      {isLoading ? (
        <SpinnerCenter />
      ) : data?.data.length === 0 ? (
        <EmptyState
          icon={Store}
          title="No stores found"
          description={
            status === "inactive" && inactiveSub === "pending-approval"
              ? "No stores pending approval."
              : status === "inactive"
                ? "No inactive stores."
                : `No ${status} stores.`
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {data?.data.map((store) => (
            <StoreCard
              key={store.id}
              store={store}
              onClick={() => void navigate(`/stores/${store.id}`)}
            />
          ))}
        </div>
      )}

      {data && (
        <Pagination
          offset={offset}
          limit={PAGE_SIZE}
          total={data.total}
          onOffsetChange={setOffset}
        />
      )}
    </div>
  );
}
